import { useMutation } from '@tanstack/react-query';
import { Lock, Sparkles } from 'lucide-react';
import { Badge, Button, Card, CardContent, useToast } from '@buzzbee/ui';
import type { ReactNode } from 'react';
import { ApiError, errorMessage, post, type UpgradeInfo } from '../lib/api';
import { useMe, useRefreshMe, type Product, type WorkspaceSummary } from '../lib/session';

export { Logo } from './Logo';

/**
 * "X is part of ReceptionistBee" with the right action for standalone vs Hive-managed workspaces.
 * `title` / `detail` replace the heading and the default description (e.g. the dashboard's
 * "what you missed this week" lines).
 */
export function UpgradePrompt({ upgrade, compact, title, detail }: { upgrade: UpgradeInfo; compact?: boolean; title?: string; detail?: ReactNode }) {
  const me = useMe();
  const refresh = useRefreshMe();
  const { success, error } = useToast();
  const ws = me.data?.current;
  const hive = ws?.managedBy === 'hive';
  const canManage = ws?.role === 'owner' || ws?.role === 'admin';
  const upgradeMut = useMutation({
    mutationFn: () => post<{ url: string | null; activated?: boolean }>('/api/billing/checkout', { plan: upgrade.plan }),
    onSuccess: (r) => {
      if (r.url) window.location.href = r.url;
      else {
        success(`You're on ${upgrade.planName}`);
        void refresh();
      }
    },
    onError: (e) => error('Upgrade failed', errorMessage(e)),
  });
  const action = hive ? (
    ws?.hive?.billingUrl ? (
      <Button asChild size={compact ? 'sm' : 'md'}>
        <a href={ws.hive.billingUrl}>Upgrade in Hive</a>
      </Button>
    ) : null
  ) : canManage ? (
    <Button size={compact ? 'sm' : 'md'} loading={upgradeMut.isPending} onClick={() => upgradeMut.mutate()}>
      <Sparkles /> Upgrade to {upgrade.planName}
    </Button>
  ) : (
    <span className="text-sm text-muted-foreground">Ask an owner to upgrade.</span>
  );
  return (
    <Card className={compact ? '' : 'mx-auto max-w-xl'}>
      <CardContent className={`flex flex-col items-start gap-3 ${compact ? 'p-5' : 'p-8'}`}>
        <Badge variant="primary">
          <Lock className="size-3" /> {upgrade.productName}
        </Badge>
        <h3 className="text-lg font-semibold">{title ?? upgrade.label}</h3>
        {detail ?? (
          <p className="text-sm text-muted-foreground">
            {upgrade.label} is part of <strong>{upgrade.planName}</strong>
            {upgrade.product === 'receptionistbee' ? ', the premium AI front desk with multilingual calls, reminders, no-show rescue, reviews and analytics.' : '.'}
          </p>
        )}
        {action}
      </CardContent>
    </Card>
  );
}

/** Render children only when the plan includes `feature`; otherwise the upgrade prompt. */
export function FeatureGate({ ws, feature, children }: { ws: WorkspaceSummary; feature: string; children: ReactNode }) {
  if (ws.entitlements.features.includes(feature)) return <>{children}</>;
  const up = ws.locked.find((l) => l.feature === feature);
  return up ? <UpgradePrompt upgrade={up} compact /> : null;
}

export function upgradeFromError(e: unknown): UpgradeInfo | null {
  return e instanceof ApiError ? e.upgrade : null;
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
        <p className="figures mt-1 text-[28px] font-semibold leading-tight tracking-[-0.02em]">{value}</p>
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

/** The AI call quality score (0-100); "Needs review" when it is low or something went wrong. */
export function ScoreBadge({ score, needsReview }: { score: number; needsReview?: boolean }) {
  const variant = needsReview ? 'warning' : score >= 80 ? 'success' : 'default';
  return (
    <Badge variant={variant} title="Call quality score">
      <span className="figures">{score}</span>
      {needsReview ? ' · Needs review' : null}
    </Badge>
  );
}

export const OUTCOME_LABELS: Record<string, { label: string; variant: 'success' | 'info' | 'warning' | 'default' | 'destructive' | 'primary' }> = {
  booked: { label: 'Booked', variant: 'success' },
  message: { label: 'Message', variant: 'info' },
  escalated: { label: 'Needs a person', variant: 'warning' },
  transferred: { label: 'Transferred', variant: 'primary' },
  answered: { label: 'Answered', variant: 'default' },
  abandoned: { label: 'Hung up', variant: 'default' },
  other: { label: 'Call', variant: 'default' },
  in_progress: { label: 'In progress', variant: 'info' },
};

export function OutcomeBadge({ outcome }: { outcome: string }) {
  const o = OUTCOME_LABELS[outcome] ?? OUTCOME_LABELS.other!;
  return <Badge variant={o.variant}>{o.label}</Badge>;
}
